import styled, { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #1a1a1a;
    color: #ffffff;
    overflow-x: hidden;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  /* Glassmorphism utility classes */
  .glass {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  .glass-dark {
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .glass-white {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  /* 3D Transform utilities */
  .transform-3d {
    transform-style: preserve-3d;
    perspective: 1000px;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #8B0000, #FFD700);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #FFD700, #FFC300);
  }
`;

export const theme = {
  colors: {
    // CHEESE-INSPIRED BRAND COLORS
    primary: '#8B0000', // Royal red (deep burgundy)
    secondary: '#FFD700', // Cheddar gold
    accent: '#10B981', // Keep green for success states
    background: '#1a1a1a',
    cardBackground: '#2d2d2d',
    
    // CHEESE PALETTE
    cheese: {
      cheddar: '#FFD700',      // Sharp cheddar golden
      aged: '#F4D03F',         // Aged cheese yellow  
      melted: '#FFC300',       // Melted cheese glow
      cream: '#FFF8DC',        // Swiss cheese cream
      parmesan: '#DAA520',     // Parmesan aged gold
      gouda: '#FFBF00'         // Gouda amber tone
    },
    
    text: {
      primary: '#ffffff',
      secondary: '#d1d5db',
      muted: '#9ca3af'
    },
    gradients: {
      // CHEESE-INSPIRED GRADIENTS
      primary: 'linear-gradient(135deg, #8B0000 0%, #FFD700 100%)', // Royal cheese
      secondary: 'linear-gradient(135deg, #FFD700 0%, #FFC300 100%)', // Cheese melt
      accent: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      
      // SPECIALIZED CHEESE GRADIENTS
      cheddarMelt: 'linear-gradient(135deg, #FFD700 0%, #FFC300 100%)',
      agedCheddar: 'linear-gradient(135deg, #F4D03F 0%, #DAA520 100%)',
      royalCheese: 'linear-gradient(135deg, #8B0000 0%, #FFD700 100%)',
      premiumGold: 'linear-gradient(135deg, #FFBF00 0%, #F4D03F 100%)',
      
      // SUPPORTING GRADIENTS  
      purple: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      orange: 'linear-gradient(135deg, #FFD700 0%, #FFC300 100%)', // Cheese-inspired
      pink: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
      blue: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      teal: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'
    }
  },
  fonts: {
    heading: "'Playfair Display', serif",
    body: "'Inter', sans-serif"
  },
  shadows: {
    small: '0 2px 4px rgba(0, 0, 0, 0.1)',
    medium: '0 4px 6px rgba(0, 0, 0, 0.1)',
    large: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)'
  },
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '16px',
    xl: '24px'
  }
};

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

export const Button = styled.button<{
  variant?: 'primary' | 'secondary' | 'accent' | 'outline';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}>`
  padding: ${props => {
    switch (props.size) {
      case 'small': return '0.5rem 1rem';
      case 'large': return '1rem 2rem';
      default: return '0.75rem 1.5rem';
    }
  }};
  border: none;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  background: ${props => {
    switch (props.variant) {
      case 'secondary': return props.theme.colors.gradients.secondary;
      case 'accent': return props.theme.colors.gradients.accent;
      case 'outline': return 'transparent';
      default: return props.theme.colors.gradients.primary;
    }
  }};
  
  color: ${props => props.variant === 'outline' ? props.theme.colors.primary : '#ffffff'};
  border: ${props => props.variant === 'outline' ? `2px solid ${props.theme.colors.primary}` : 'none'};

  -webkit-tap-highlight-color: transparent;
  
  /* Mobile-first: Active state for touch */
  &:active {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  /* Desktop-only: Hover effects only for devices with fine pointers */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.shadows.large};
    }
  }
`;

export const Card = styled.div<{
  padding?: 'small' | 'medium' | 'large';
  borderColor?: string;
}>`
  background: ${props => props.theme.colors.cardBackground};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => {
    switch (props.padding) {
      case 'small': return '1rem';
      case 'large': return '2rem';
      default: return '1.5rem';
    }
  }};
  box-shadow: ${props => props.theme.shadows.medium};
  border: ${props => props.borderColor ? `2px solid ${props.borderColor}` : 'none'};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

export const Grid = styled.div<{
  columns?: number;
  gap?: string;
}>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.gap || '1.5rem'};
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => props.columns ? `repeat(${props.columns}, 1fr)` : 'repeat(auto-fit, minmax(300px, 1fr))'};
  }
`;

export const Badge = styled.span<{
  variant?: 'primary' | 'secondary' | 'accent' | 'warning' | 'info';
}>`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius.xl};
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  
  background: ${props => {
    switch (props.variant) {
      case 'secondary': return 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)';
      case 'accent': return 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
      case 'warning': return 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)';
      case 'info': return 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)';
      default: return 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)';
    }
  }};
  
  color: #ffffff;
`; 